
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { NavItem } from '../types';

interface TooltipSubmenuProps {
  item: NavItem;
  badgeCount: number | null;
  isChildActive: boolean;
  handleNavigation: () => void;
}

const TooltipSubmenu: React.FC<TooltipSubmenuProps> = ({
  item,
  badgeCount,
  isChildActive,
  handleNavigation
}) => {
  const Icon = item.icon;
  const [isOpen, setIsOpen] = useState(false);
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleChildNavigation = () => {
    setIsOpen(false);
    handleNavigation();
  };
  
  return (
    <TooltipProvider delayDuration={0}>
      <div className="relative space-y-1">
        <Tooltip open={isOpen} onOpenChange={setIsOpen}>
          <TooltipTrigger asChild>
            <button
              onClick={handleClick}
              className={cn(
                "flex items-center justify-center w-full px-3 py-2.5 rounded-md transition-colors duration-200 group relative",
                isChildActive ? "text-primary font-medium" : "text-muted-foreground",
                "hover:bg-muted hover:text-foreground"
              )}
            >
              <Icon size={20} className="flex-shrink-0" />
              {badgeCount !== null && (
                <Badge variant="default" className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center text-xs">{badgeCount}</Badge>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="p-0 bg-card border shadow-md w-56">
            <div className="py-1 flex flex-col">
              <div className="px-3 py-2 font-medium border-b">{item.title}</div>
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                {item.children.map((child) => (
                  <NavLink
                    key={child.path}
                    to={child.path}
                    className={({ isActive }) => cn(
                      "flex items-center px-3 py-2 transition-colors duration-200",
                      isActive 
                        ? "bg-primary/10 text-primary font-medium" 
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                    onClick={handleChildNavigation}
                    end={child.path === '/dashboard' || child.path === '/'}
                  >
                    {child.icon && <child.icon size={16} className="mr-2 flex-shrink-0" />}
                    <span className="flex-1">{child.title}</span>
                    {child.badge && (
                      <Badge variant="default" className="ml-auto">{child.badge}</Badge>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default TooltipSubmenu;
